/*
 * Copyright (c) 2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlaskConical } from 'lucide-react';
import { isSandbox } from '@/config/sandbox';

/**
 * The strip that tells a visitor they are registering in the sandbox.
 *
 * Placed at the top of every screen that creates an account — driver, owner, partner — because that
 * is the moment somebody could otherwise believe they have signed up for the real product. Renders
 * NOTHING in production, so the live registration screens are untouched.
 */
export const SandboxBanner: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();

  if (!isSandbox()) return null;

  return (
    <div
      role="status"
      className={`w-full bg-amber-400 text-amber-950 ${className ?? ''}`}
    >
      <div className="max-w-2xl mx-auto flex items-start gap-2 px-4 py-2">
        <FlaskConical className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden />
        <p className="text-xs sm:text-sm leading-snug">
          <span className="font-bold uppercase tracking-wide">
            {t('sandbox.badge', 'Sandbox')}
          </span>
          {' — '}
          {t(
            'sandbox.notice',
            'Test environment. Accounts created here are not real and no payment is ever charged.',
          )}
        </p>
      </div>
    </div>
  );
};

export default SandboxBanner;
